import api from '../utils/api.js';

/**
 * 求職者履歴書 (attachments の file_category=RESUME) の API 呼び出しと、
 * 「テキストを読み取れたか」(text_extracted) の判定を集約する層 (Issue #1057)。
 *
 * エンドポイントは backend/app/routers/opportunities.py の
 * GET /opportunities/{opportunity_id}/resume (応答は ResumeListResponse)。
 *
 * text_extracted は「そのファイルからテキストを読み取って保存できているか」であり、
 * 「AI が実際に使ったか」ではない (backend/app/models/schemas.py の
 * ResumeAttachmentResponse の docstring)。読み取れなかった履歴書も、推薦理由・
 * おすすめポイントの生成にはファイルのまま渡されるため「使われていない」とは書かない
 * (PBI-002_類似度検索アルゴリズム設計.md §2.1.1、Issue #1057)。
 */

/**
 * 読み取れなかった履歴書に出す注記 (Issue #1057 で PM 確定)。
 *
 * badge は短縮形で、full は全文。短縮形だけを出さない (全文の併記が必要)。
 */
export const RESUME_NOT_EXTRACTED_NOTICE = {
    badge: 'AI マッチングに一部未反映',
    full:
        'この履歴書はテキストを読み取れなかったため、AI マッチングに十分反映されていません。'
        + '削除して入れ直すと、もう一度読み取ります。',
};

/**
 * 当該履歴書が「読み取れなかった」と判定できるか。
 *
 * text_extracted が無い応答 (本項目より前の BE) では判定しない。項目の不在を false と
 * 解釈すると全件に注記が出るため、真偽値として false のときだけ true を返す
 * (Issue #1057 AC ③、画面レイアウト_SCR-M-001.md §6.3 の注記)。
 *
 * @param {{text_extracted?: boolean}|null|undefined} attachment
 * @returns {boolean}
 */
export function isResumeTextMissing(attachment) {
    return attachment?.text_extracted === false;
}

/**
 * 「読み取れた履歴書があるか」。AI マッチングの事前バリデーションの 3 つ目の条件
 * (画面レイアウト_SCR-M-001.md §6.3、Issue #1043 で BE の実行条件が広がったことへの追随)。
 *
 * ⚠ **null は「古い BE で判定できない」だけを表す。「未取得」と混ぜないこと。**
 * 設計書 §6.3 のとおり、2 つは扱いが違う:
 *   - **一覧が未取得・取得失敗** → 判定を保留し、実行をブロックしない
 *   - **一覧は取れたが text_extracted が無い (古い BE)** → 履歴書を条件に入れない
 *
 * 本関数は配列でない入力にも防御的に null を返すが、**未取得かどうかは呼び出し側が
 * Array.isArray で先に分けること**。`hasExtractedResume(x) === null` だけで分岐すると
 * 上の 2 つが 1 つに潰れ、未取得の案件で実行を止める side に倒れる (エラーにはならない)。
 *
 * 履歴書が 0 件のときは「読み取れた履歴書は無い」と判定できるため false を返す
 * (null ではない)。
 *
 * @param {object[]|null|undefined} attachments 配列であること (未取得は呼び出し側で分ける)
 * @returns {boolean|null} null = 古い BE で判定できない
 */
export function hasExtractedResume(attachments) {
    if (!Array.isArray(attachments)) return null;
    if (attachments.length === 0) return false;
    const known = attachments.filter((att) => typeof att?.text_extracted === 'boolean');
    if (known.length === 0) return null;
    return known.some((att) => att.text_extracted === true);
}

/**
 * 当該案件の履歴書一覧を取得する。
 *
 * 失敗は errorMessage を返さず attachments を null にする。呼び出し側は事前
 * バリデーションの判定を保留するだけで、利用者に見せるエラーは出さない
 * (履歴書一覧そのものは基本情報タブ側で表示しており、ここでの取得は判定のため)。
 *
 * @param {string} opportunityId
 * @returns {Promise<{ok: boolean, attachments: object[]|null}>}
 */
export async function fetchResumeAttachments(opportunityId) {
    let response;
    try {
        response = await api.fetch(`/opportunities/${opportunityId}/resume`);
    } catch {
        return { ok: false, attachments: null };
    }

    if (!response || !response.ok) {
        return { ok: false, attachments: null };
    }

    try {
        const data = await response.json();
        // attachments が配列でない応答は「0 件」ではなく「取れなかった」として扱う。
        // [] にすると履歴書 0 件として条件に入り、実行を止める側に倒れる
        if (!Array.isArray(data?.attachments)) {
            return { ok: false, attachments: null };
        }
        return { ok: true, attachments: data.attachments };
    } catch {
        return { ok: false, attachments: null };
    }
}
